import { createServerFn } from "@tanstack/react-start";
import { getRequestIP } from "@tanstack/react-start/server";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { sendTransactionalEmailServer } from "@/lib/email/send.server";
import { SITE_URL } from "@/lib/seo";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function clean(s: unknown, max = 255): string | null {
  if (typeof s !== "string") return null;
  const v = s.trim();
  if (!v) return null;
  return v.slice(0, max);
}
function cleanEmail(s: unknown): string | null {
  const v = clean(s, 255);
  if (!v || !EMAIL_RE.test(v)) return null;
  return v.toLowerCase();
}
function cleanIds(arr: unknown): string[] {
  if (!Array.isArray(arr)) return [];
  return arr.filter((x) => typeof x === "string" && x.length < 100).slice(0, 100);
}

// Simple in-memory rate limiter (per-instance; resets on deploy/restart).
// Mitigates email-spam abuse of unauthenticated save/share endpoints.
const rateBuckets = new Map<string, { count: number; resetAt: number }>();
const RATE_WINDOW_MS = 15 * 60 * 1000;
const RATE_MAX = 5;

function checkRate(key: string): { ok: boolean; retryInMin?: number } {
  const now = Date.now();
  const entry = rateBuckets.get(key);
  if (!entry || entry.resetAt < now) {
    rateBuckets.set(key, { count: 1, resetAt: now + RATE_WINDOW_MS });
    return { ok: true };
  }
  if (entry.count >= RATE_MAX) {
    return { ok: false, retryInMin: Math.ceil((entry.resetAt - now) / 60000) };
  }
  entry.count += 1;
  return { ok: true };
}

// Per-recipient cap: prevent abusing share/save to spam a single inbox.
const RECIPIENT_WINDOW_MS = 24 * 60 * 60 * 1000;
const RECIPIENT_MAX = 5;
function checkRecipientRate(email: string): { ok: boolean; retryInMin?: number } {
  const now = Date.now();
  const key = `recipient:${email}`;
  const entry = rateBuckets.get(key);
  if (!entry || entry.resetAt < now) {
    rateBuckets.set(key, { count: 1, resetAt: now + RECIPIENT_WINDOW_MS });
    return { ok: true };
  }
  if (entry.count >= RECIPIENT_MAX) {
    return { ok: false, retryInMin: Math.ceil((entry.resetAt - now) / 60000) };
  }
  entry.count += 1;
  return { ok: true };
}

function callerIp(): string {
  try {
    return getRequestIP({ xForwardedFor: true }) || "unknown";
  } catch {
    return "unknown";
  }
}

interface SavePlanInput {
  eventIds: string[];
  wizardData?: unknown;
  saverEmail?: string;
}

export const savePlan = createServerFn({ method: "POST" })
  .inputValidator((input: SavePlanInput) => ({
    eventIds: cleanIds(input?.eventIds),
    wizardData: input?.wizardData ?? null,
    saverEmail: cleanEmail(input?.saverEmail),
  }))
  .handler(async ({ data }) => {
    if (data.eventIds.length === 0) return { ok: false as const, error: "No events provided" };

    const rate = checkRate(`save:${callerIp()}`);
    if (!rate.ok) {
      return { ok: false as const, error: `Too many requests. Try again in ${rate.retryInMin} minutes.` };
    }

    const { data: plan, error } = await supabaseAdmin
      .from("saved_plans")
      .insert({
        event_ids: data.eventIds,
        wizard_data: (data.wizardData as any) ?? null,
      })
      .select("share_token")
      .single();

    if (error || !plan?.share_token) {
      return { ok: false as const, error: error?.message || "Failed to save plan" };
    }

    if (data.saverEmail) {
      const recipRate = checkRecipientRate(data.saverEmail);
      if (!recipRate.ok) {
        return { ok: false as const, error: "This email has received too many plan saves recently. Try again later." };
      }
      await supabaseAdmin.from("users").upsert({ email: data.saverEmail }, { onConflict: "email" });
      const shareUrl = `${SITE_URL}/plan?plan=${plan.share_token}`;
      await sendTransactionalEmailServer({
        templateName: "save-plan",
        recipientEmail: data.saverEmail,
        idempotencyKey: `save-plan-${plan.share_token}`,
        templateData: { shareUrl },
      });
    }

    return { ok: true as const, shareToken: plan.share_token };
  });

interface SharePlanInput {
  eventIds: string[];
  fromEmail?: string;
  toEmail: string;
}

export const sharePlan = createServerFn({ method: "POST" })
  .inputValidator((input: SharePlanInput) => ({
    eventIds: cleanIds(input?.eventIds),
    fromEmail: cleanEmail(input?.fromEmail),
    toEmail: cleanEmail(input?.toEmail),
  }))
  .handler(async ({ data }) => {
    if (data.eventIds.length === 0) return { ok: false as const, error: "No events provided" };
    if (!data.toEmail) return { ok: false as const, error: "Recipient email required" };

    const rate = checkRate(`share:${callerIp()}`);
    if (!rate.ok) {
      return { ok: false as const, error: `Too many requests. Try again in ${rate.retryInMin} minutes.` };
    }
    const recipRate = checkRecipientRate(data.toEmail);
    if (!recipRate.ok) {
      return { ok: false as const, error: "That recipient has received too many shares recently. Try again later." };
    }

    const { data: plan, error } = await supabaseAdmin
      .from("saved_plans")
      .insert({
        event_ids: data.eventIds,
        wizard_data: null,
      })
      .select("share_token")
      .single();

    if (error || !plan?.share_token) {
      return { ok: false as const, error: error?.message || "Failed to create share link" };
    }

    if (data.fromEmail) {
      await supabaseAdmin.from("users").upsert({ email: data.fromEmail }, { onConflict: "email" });
    }
    await supabaseAdmin.from("users").upsert({ email: data.toEmail }, { onConflict: "email" });

    const shareUrl = `${SITE_URL}/plan?plan=${plan.share_token}`;
    await sendTransactionalEmailServer({
      templateName: "share-plan",
      recipientEmail: data.toEmail,
      idempotencyKey: `share-plan-${plan.share_token}-${data.toEmail}`,
      templateData: { shareUrl, fromEmail: data.fromEmail || undefined },
    });

    return { ok: true as const, shareToken: plan.share_token };
  });

// ---------- Read a saved plan by share token (for /plan?plan=...) ----------
export const getPlanByToken = createServerFn({ method: "GET" })
  .inputValidator((input: { token: string }) => ({
    token: clean(input?.token, 128) ?? "",
  }))
  .handler(async ({ data }) => {
    if (!data.token) return { ok: false as const, error: "Missing token" };
    const { data: plan, error } = await supabaseAdmin
      .from("saved_plans")
      .select("event_ids, wizard_data")
      .eq("share_token", data.token)
      .maybeSingle();
    if (error || !plan) return { ok: false as const, error: "Plan not found" };
    const ids = Array.isArray(plan.event_ids) ? plan.event_ids : [];
    if (ids.length === 0) return { ok: true as const, events: [], wizardData: plan.wizard_data ?? null };
    const { data: rows, error: evErr } = await supabaseAdmin
      .from("events")
      .select("*")
      .in("id", ids)
      .eq("is_active", true);
    if (evErr) return { ok: false as const, error: evErr.message };
    // Preserve original event order from saved_plans.event_ids
    const byId = new Map((rows ?? []).map((r) => [r.id, r]));
    const ordered = ids.map((id) => byId.get(id)).filter(Boolean);
    return { ok: true as const, events: ordered, wizardData: plan.wizard_data ?? null };
  });
